import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderService } from '../../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { BaseComponent } from '../../../core/base.component';
import { NotificationService } from '../../../notification/notification.service';
import { AuthService } from '../../../core/security/auth.service';
import { ContactService } from '../../contact.service';
import { RolesEnum } from '../../../core/security/roles.enum';
import { ContactSyncModel } from '../../../api/models/contact-sync-model';
import { MatConfirmDialogComponent } from '../../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ModulesEnum } from '../../../core/security/modules.enum';
import { TagsGridDialogComponent } from '../../../tags/tags-list-dialog.component';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { CustomFieldModel } from '../../../api/models/custom-field-model';
import { uniq, uniqBy } from 'lodash';
import { take } from 'rxjs';
import { GOOGLE_CUSTOM_FIELDS } from '../custom-fields/google-custom-fields';
import { HHA_CUSTOM_CAREGIVER_FIELDS, HHA_CUSTOM_PATIENT_FIELDS } from '../custom-fields/hha-exchange-fields';
import { CurrentUser } from '../../../api/models/current-user';
import { MatCard, MatCardContent, MatCardTitle, MatCardActions } from '@angular/material/card';
import { FlexModule } from '@angular/flex-layout';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatAccordion } from '@angular/material/expansion';
import { IntegrationCustomFieldSettingsComponent } from './integration-custom-field-settings/integration-custom-field-settings.component';
import { MatButton, MatAnchor } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { LongTaskButtonComponent } from '../../../core/visual/long-task-button/long-task-button.component';
import { DateTimePipe } from '../../../core/pipes/date-time.pipe';

@Component({
    selector: 'app-contact-single-integration',
    templateUrl: 'contact-single-integration.component.html',
    styleUrls: ['contact-single-integration.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatCard,
        MatCardContent,
        FlexModule,
        MatCardTitle,
        NgClass,
        MatCheckbox,
        MatAccordion,
        NgFor,
        IntegrationCustomFieldSettingsComponent,
        MatCardActions,
        NgIf,
        MatButton,
        MatTooltip,
        Angular2PromiseButtonModule,
        LongTaskButtonComponent,
        MatAnchor,
        DateTimePipe,
    ],
})
export class ContactSingleIntegrationComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInput: any;

  @Input() integration: ContactSyncModel;
  @Input() customFieldData: CustomFieldModel[];

  @Output() integrationChanged = new EventEmitter<ContactSyncModel>();

  public integrationTitle = '';
  public integrationHasLogin = false;
  public canEditTags = false;
  public submitted: boolean;
  public buttonSubmitPromise: Promise<void>;
  public buttonSyncNowPromise: Promise<void>;

  public readonly searchControls: FormControl[] = [];
  public readonly hhaSyncProvider = 'hhaExchange';
  public readonly googleSyncProvider = 'google';
  public readonly preselectedFields = [
    'PatientID',
    'EmergencyContacts.EmergencyContact.Phone1',
    'EmergencyContacts.EmergencyContact.Phone2',
  ];

  private hhaCustomFieldSectionsForm: FormGroup;
  private itemToRevoke: ContactSyncModel;
  private user?: CurrentUser;

  public readonly _filteredSectionFields: Map<number, UntypedFormControl[]> = new Map();
  private readonly _hhaPatientFields = HHA_CUSTOM_PATIENT_FIELDS;
  private readonly _hhaCaregiverFields = HHA_CUSTOM_CAREGIVER_FIELDS;
  private readonly _googleCustomFields = GOOGLE_CUSTOM_FIELDS;

  constructor(
    public readonly dialog: MatDialog,
    public readonly contactService: ContactService,
    public readonly route: ActivatedRoute,
    public readonly headerService: HeaderService,
    public readonly titleService: Title,
    public readonly authService: AuthService,
    private readonly _router: Router,
    private readonly _notificationService: NotificationService,
    private readonly _fb: UntypedFormBuilder,
    private readonly _cdr: ChangeDetectorRef,
  ) {
    super(route, headerService, titleService, authService);

    this.viewRoles = [];
    this.editRoles = [];
    this.deleteRoles = [];
  }

  onCurrentUserChanged(user?: CurrentUser) {
    this.user = user;
  }

  get customFieldSections(): UntypedFormArray {
    return this.hhaCustomFieldSectionsForm?.get('sections') as UntypedFormArray;
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this.createCustomFieldsForm(this.customFieldData);

    this.asyncInit().then(() => {
      this.ngOnChanges();
    });
  }

  public ngOnChanges(changes?: SimpleChanges): void {
    console.log('ngOnChanges');
    this.updateViewData();
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.headerService.showBackButton(false);
  }

  public async processSubmit(model: ContactSyncModel, customFieldIdsToDelete: string[]): Promise<void> {
    const customFields = this.mapCustomFields();

    const requestSucceeded = await this.contactService.saveSettings({
      ...model,
      customFields,
      customFieldIdsToDelete,
    });
    if (requestSucceeded) {
      this._notificationService.notifySuccess('Settings Has Been Saved Successfully');
    } else {
      this._notificationService.notifyDanger('Unable to save integration settings');
    }
    this.integrationChanged.emit(model);
  }

  public revokeToken($event: MouseEvent, integration: ContactSyncModel): void {
    this.itemToRevoke = integration;

    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Revoke integration token?',
        text: 'Are you sure you want to revoke integration token?',
        actionTitle: 'Revoke',
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        if (this.itemToRevoke.syncProvider === 'google') {
          await this.contactService.revokeGoogleIntegration();
        } else if (this.itemToRevoke.syncProvider === 'microsoft') {
          await this.contactService.revokeMicrosoftIntegration();
        }

        this.itemToRevoke = null;

        this._notificationService.notifySuccess('Token was revoked successfully');
        this.integrationChanged.emit(integration);
      }
    });
  }

  public saveChanges($event: MouseEvent, integration: ContactSyncModel): void {
    console.log(integration);
    const customFieldIdsToDelete = this.getCustomFieldToDeleteIds();

    if (!customFieldIdsToDelete.length) {
      this.buttonSubmitPromise = this.processSubmit(integration, customFieldIdsToDelete);
      return;
    }

    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',

      data: {
        title: 'Confirm Save?',
        text: 'Some custom fields that were created previously are now unchecked. Once saved, these fields will be deleted. Are you sure you want to proceed?',
        actionTitle: 'Save',
        showCancelBtn: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(confirmed => {
        if (confirmed) {
          this.buttonSubmitPromise = this.processSubmit(integration, customFieldIdsToDelete);
          this._cdr.detectChanges();
        }
      });
  }

  public async syncNowTask(): Promise<void> {
    await this.syncContactsNowAction(this.integration);
  }

  public onSyncNowClick($event: MouseEvent, integration: ContactSyncModel): void {
    console.log('Sync Now Button Clicked');
    this.buttonSyncNowPromise = this.syncContactsNowAction(integration);
  }

  public syncDailyUpdated(integration: ContactSyncModel): void {
    if (integration.syncInterval > 0) {
      integration.syncInterval = 0;
    } else {
      integration.syncInterval = 3600;
    }
  }

  public activateSyncIntegration(integration: ContactSyncModel): void {
    integration.isActive = true;
    this.saveChanges(null, integration);
  }

  public deactivateSyncIntegration(integration: ContactSyncModel): void {
    integration.isActive = false;
    this.saveChanges(null, integration);
  }

  public onManageTagsClick($event: MouseEvent): void {
    $event.stopPropagation();
    const dialogRef = this.dialog.open(TagsGridDialogComponent, {
      width: '600px',
      height: '90%',
    });

    dialogRef.afterClosed().subscribe(async () => {
      await this.contactService.getTags();
    });
  }

  public isHhaSection(sectionGroup: AbstractControl, title: string): boolean {
    return sectionGroup.get('title').value === title;
  }

  public trackByFn(index: number, item: any): number {
    return index;
  }

  private isPreselectedCustomField(fieldKey: string): boolean {
    return this.preselectedFields.includes(fieldKey);
  }

  private async asyncInit(): Promise<void> {
    const authCode = this.route.snapshot.queryParams['code'];
    const provider = this.route.snapshot.queryParams['provider'];

    if (authCode && (this.integration.syncProvider === 'google' || this.integration.syncProvider === 'microsoft')) {
      if ((!provider || provider === 'google') && this.integration.syncProvider === 'google') {
        await this.contactService.saveGoogleAuthCode(authCode);
        this.integrationChanged.emit(this.integration);
      } else if (provider === 'microsoft' && this.integration.syncProvider === 'microsoft') {
        await this.contactService.saveMicrosoftAuthCode(authCode);
        this.integrationChanged.emit(this.integration);
      }

      await this._router.navigate(['/contacts/import']);
    } else {
      await this.contactService.getTags();
    }
  }

  private async syncContactsNowAction(integration: ContactSyncModel): Promise<void> {
    await this.contactService.syncContacts(integration.syncProvider);
    this._notificationService.notifySuccess('Contacts synchronization successfully completed');
    this.integrationChanged.emit(this.integration);
  }

  private createCustomFieldsForm(customFieldData: CustomFieldModel[]): void {
    this.hhaCustomFieldSectionsForm = this._fb.group({
      sections: this._fb.array(
        this.getSectionsByIntegration().map(section => this.createSectionGroup(section, customFieldData || [])),
      ),
    });
  }

  private updateViewData(): void {
    this.canEditTags =
      this.authService.hasAccess([RolesEnum.Tags_Edit, RolesEnum.Tags_Delete]) &&
      this.authService.userHasPackage(ModulesEnum.TagsModule);

    switch (this.integration.syncProvider) {
      case 'google':
        this.integrationTitle = 'Google Contacts';
        this.integrationHasLogin = true;
        this.viewRoles = [
          RolesEnum.PrivateContact_View,
          RolesEnum.PrivateContact_Edit,
          RolesEnum.PrivateContact_Delete,
        ];
        this.editRoles = [RolesEnum.PrivateContact_Edit, RolesEnum.PrivateContact_Delete];
        this.deleteRoles = [RolesEnum.PrivateContact_Delete];
        break;
      case 'microsoft':
        this.integrationTitle = 'Microsoft 365';
        this.integrationHasLogin = true;
        this.viewRoles = [
          RolesEnum.PrivateContact_View,
          RolesEnum.PrivateContact_Edit,
          RolesEnum.PrivateContact_Delete,
        ];
        this.editRoles = [RolesEnum.PrivateContact_Edit, RolesEnum.PrivateContact_Delete];
        this.deleteRoles = [RolesEnum.PrivateContact_Delete];
        break;
      case 'telegoPbx':
        this.integrationTitle = 'TeleGo Phone System';
        this.integrationHasLogin = false;
        this.viewRoles = [
          RolesEnum.CompanyContact_View,
          RolesEnum.CompanyContact_Edit,
          RolesEnum.CompanyContact_Delete,
        ];
        this.editRoles = [RolesEnum.CompanyContact_Edit, RolesEnum.CompanyContact_Delete];
        this.deleteRoles = [RolesEnum.CompanyContact_Delete];
        break;
      case this.hhaSyncProvider:
        this.integrationTitle = 'HHAeXchange';
        this.integrationHasLogin = false;
        this.viewRoles = [RolesEnum.HhaView, RolesEnum.HhaEdit, RolesEnum.HhaDelete];
        this.editRoles = [RolesEnum.HhaEdit, RolesEnum.HhaDelete];
        this.deleteRoles = [RolesEnum.HhaDelete];

        if (!this.integration.settings) {
          this.integration.settings = {
            syncHhaCaregivers: false,
            syncHhaPatients: false,
            hhaCaregiversTags: [],
            hhaPatientsTags: [],
          };
        }

        break;
    }
  }

  private getSectionsByIntegration(): { title: string; fields: Partial<CustomFieldModel>[] }[] {
    let fields = [];

    switch (this.integration.syncProvider) {
      case this.hhaSyncProvider:
        fields = [
          {
            title: 'Sync HHAeXchange Caregivers',
            fields: this._hhaCaregiverFields,
          },
          {
            title: 'Sync HHAeXchange Patients',
            fields: this._hhaPatientFields,
          },
        ];
        break;
      case this.googleSyncProvider:
        fields = [
          {
            title: 'Google Custom Fields',
            fields: this._googleCustomFields,
          },
        ];
        break;
      default:
        break;
    }

    return fields;
  }

  private createSectionGroup(section, customFields: CustomFieldModel[]): UntypedFormGroup {
    const sectionGroup = this._fb.group({
      title: [section.title],
      fields: this._fb.array(
        section.fields.map(field =>
          this.createFieldGroup(
            field,
            customFields.find(x => x.fieldKey === field.fieldKey),
          ),
        ),
      ),
    });

    return sectionGroup;
  }

  private createFieldGroup(field, customFieldData: CustomFieldModel): UntypedFormGroup {
    const { id, fieldKey, includeInAnySearch } = customFieldData || {};
    const isPreselected = this.isPreselectedCustomField(field.fieldKey);

    return this._fb.group({
      id: [id || 'new'],
      fieldTitle: [field.fieldTitle],
      fieldType: [field.fieldType],
      fieldKey: [field.fieldKey],
      fieldFormat: [field.fieldFormat],
      //NOTE: If customKey exists mark it as already enabled
      includeInSearch: [{ value: !!includeInAnySearch || isPreselected, disabled: isPreselected || !fieldKey }],
      enableField: [{ value: !!fieldKey || isPreselected, disabled: isPreselected }],
    });
  }

  private getCustomFieldToDeleteIds(): string[] {
    const sectionsData = this.customFieldSections?.getRawValue();

    //NOTE: Unique since some fields may share the same ID.
    return uniq(
      (sectionsData || [])
        .flatMap(section => section.fields)
        ?.filter(field => !field.enableField && field.id !== 'new')
        .map(field => field.id),
    );
  }

  private mapCustomFields(): CustomFieldModel[] {
    const sectionsData = this.customFieldSections?.getRawValue();
    const enabledFields = (sectionsData || [])
      .flatMap(section => section.fields)
      ?.filter(field => field.enableField || this.isPreselectedCustomField(field.fieldKey));

    const mappedFields = uniqBy(enabledFields, 'fieldKey').map(
      field =>
        ({
          id: field.id,
          category: 0,
          fieldKey: field.fieldKey,
          fieldTitle: field.fieldTitle,
          fieldType: field.fieldType,
          fieldFormat: field.fieldFormat,
          required: false,
          includeInSearch: false,
          includeInAnySearch: field.includeInSearch || this.isPreselectedCustomField(field.fieldKey),
          fieldOrder: 0,
        }) satisfies CustomFieldModel,
    );

    return mappedFields;
  }

  getTaskKey() {
    if (this.integration.syncProvider === 'google') {
      return `google:${this.user?.customerUserId}`;
    } else if (this.integration.syncProvider === 'microsoft') {
      return `microsoft:${this.user?.customerUserId}`;
    } else if (this.integration.syncProvider === 'telegoPbx') {
      return 'pbx';
    } else if (this.integration.syncProvider === this.hhaSyncProvider) {
      return 'hha';
    }
    return '';
  }
}
